import React, { useState, useEffect, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth, ROLE_LABELS } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Clock,
  Wallet,
  User,
  LogOut,
  Menu,
  X,
  Zap,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Play,
  Calendar
} from 'lucide-react';

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    return { theme: 'light', toggleTheme: () => {} };
  }
  return context;
};

// Theme Provider Component
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('hims-theme') || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('hims-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Logo Component
const Logo = ({ collapsed }) => (
  <div className="flex items-center gap-3">
    <div className="relative">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
        <Zap className="h-6 w-6 text-white" strokeWidth={2.5} />
      </div>
      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-900" />
    </div>
    {!collapsed && (
      <div className="overflow-hidden">
        <h1 className="font-bold text-xl tracking-tight">HIMS</h1>
        <p className="text-xs text-muted-foreground truncate">Site Sakini Paneli</p>
      </div>
    )}
  </div>
);

// Navigation Item Component
const NavItem = ({ item, isActive, collapsed, onClick }) => {
  const Icon = item.icon;
  
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`
        group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
        transition-all duration-200 relative
        ${isActive 
          ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25' 
          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
        }
        ${collapsed ? 'justify-center' : ''}
      `}
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? '' : 'group-hover:scale-110'} transition-transform`} strokeWidth={1.5} />
      {!collapsed && <span className="truncate">{item.name}</span>}
    </Link>
  );
};

// User Profile Card
const UserCard = ({ user, collapsed, onLogout }) => {
  const initials = user?.full_name
    ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className={`p-3 ${collapsed ? 'flex flex-col items-center' : ''}`}>
      {!collapsed && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-accent/30 mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm shadow-lg">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{user?.full_name || user?.email}</p>
            <p className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role] || 'Site Sakini'}</p>
          </div>
        </div>
      )}
      <button
        onClick={onLogout}
        className={`
          flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm font-medium
          text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-all duration-200
          ${collapsed ? 'justify-center' : ''}
        `}
      >
        <LogOut className="h-5 w-5" strokeWidth={1.5} />
        {!collapsed && <span>Çıkış Yap</span>}
      </button>
    </div>
  );
};

// Theme Toggle Button
const ThemeToggle = ({ collapsed }) => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className={`
        flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm font-medium
        text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-all duration-200
        ${collapsed ? 'justify-center' : ''}
      `}
    >
      {theme === 'dark' ? (
        <>
          <Sun className="h-5 w-5" strokeWidth={1.5} />
          {!collapsed && <span>Açık Tema</span>}
        </>
      ) : (
        <>
          <Moon className="h-5 w-5" strokeWidth={1.5} />
          {!collapsed && <span>Koyu Tema</span>}
        </>
      )}
    </button>
  );
};

// Main Resident Layout
const ResidentLayout = ({ children }) => {
  const { user, logout, isSimulationActive, stopRoleSimulation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  
  // Simülasyon kontrolü
  const simulationActive = isSimulationActive ? isSimulationActive() : false;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  // Simülasyonu bitir ve dashboard'a yönlendir
  const handleStopSimulation = () => {
    if (stopRoleSimulation) {
      stopRoleSimulation();
      navigate('/dashboard');
    }
  };

  // Site Sakini için menü öğeleri
  const menuItems = [
    { name: 'Dashboard', path: '/resident/dashboard', icon: LayoutDashboard },
    { name: 'Şarj Başlat', path: '/resident/charging', icon: Play },
    { name: 'Rezervasyonlar', path: '/resident/reservations', icon: Calendar },
    { name: 'Şarj Geçmişim', path: '/resident/charging-history', icon: Clock },
    { name: 'Bakiyem', path: '/resident/balance', icon: Wallet },
    { name: 'Profilim', path: '/resident/profile', icon: User }
  ];

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background">
        {/* Mobile Header */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 px-4 flex items-center justify-between bg-background/80 backdrop-blur-xl border-b border-border">
          <Logo collapsed={false} />
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-xl hover:bg-accent transition-colors"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </header>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed top-0 left-0 z-50 h-full bg-card border-r border-border
            transform transition-all duration-300 ease-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            ${collapsed ? 'w-20' : 'w-64'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className={`h-16 flex items-center ${collapsed ? 'justify-center px-2' : 'px-4'} border-b border-border`}>
              <Logo collapsed={collapsed} />
            </div>

            {/* Collapse Toggle */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-primary text-primary-foreground rounded-full items-center justify-center shadow-lg hover:scale-110 transition-transform"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto py-4 px-3">
              <div className="space-y-1">
                {menuItems.map((item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    isActive={location.pathname === item.path}
                    collapsed={collapsed}
                    onClick={() => setSidebarOpen(false)}
                  />
                ))}
              </div>
            </nav>

            {/* Footer */}
            <div className="border-t border-border">
              <div className="px-3 py-2">
                <ThemeToggle collapsed={collapsed} />
              </div>
              <UserCard user={user} collapsed={collapsed} onLogout={handleLogout} />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`
          pt-16 lg:pt-0 min-h-screen
          transition-all duration-300
          ${collapsed ? 'lg:pl-20' : 'lg:pl-64'}
        `}>
          <div className="p-4 lg:p-8">
            {/* Simülasyon Banner DashboardLayout'tan geliyor, burada tekrar gösterme */}
            {children}
          </div>
        </main>
      </div>
    </ThemeProvider>
  );
};

export default ResidentLayout;
